import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, ArrowRight, CheckCircle2, FileSpreadsheet, Upload, X } from 'lucide-react';
import type { ImportCommitResponse, ImportMode } from '@forest/contracts';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useImportCommit, useSubParcelImportParse, type SubParcelParseResponse } from './useImport';
import { useUiStore } from '@/stores/uiStore';
import { apiError } from '@/lib/api-client';
import { cn } from '@/lib/cn';
import { ImportColumnCheck, ImportProblems } from './ImportProblems';

type Step = 'upload' | 'preview' | 'done';

const REQUIRED_EXTS = ['shp', 'dbf'];
const ACCEPTED_EXTS = ['shp', 'dbf', 'prj', 'shx'];

function extOf(name: string): string {
  return name.toLowerCase().split('.').pop() ?? '';
}

const ACTION_CLASS: Record<SubParcelParseResponse['preview'][number]['action'], string> = {
  create: 'bg-emerald-100 text-emerald-700',
  update: 'bg-amber-100 text-amber-800',
  unchanged: 'bg-slate-100 text-slate-600',
};

/**
 * Subpolygon shapefile import. Each row names its full UCF (`1A1`); the
 * server finds the parent parcel (`1A`) whose outline contains it, and the
 * preview says per row whether it will be created, refreshed from this file
 * (outline / area changed) or left alone.
 */
export function SubParcelImportPanel() {
  const { t } = useTranslation(['import', 'common']);
  const navigate = useNavigate();
  const pushToast = useUiStore((s) => s.pushToast);
  const parseMut = useSubParcelImportParse();
  const commitMut = useImportCommit();

  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [step, setStep] = useState<Step>('upload');
  const [parsed, setParsed] = useState<SubParcelParseResponse | null>(null);
  const [committed, setCommitted] = useState<ImportCommitResponse | null>(null);
  const [mode, setMode] = useState<ImportMode>('skip_invalid');

  const addFiles = useCallback((list: FileList | File[] | null) => {
    if (!list) return;
    const incoming = Array.from(list).filter((f) => ACCEPTED_EXTS.includes(extOf(f.name)));
    setFiles((prev) => {
      const byExt = new Map<string, File>();
      for (const f of prev) byExt.set(extOf(f.name), f);
      for (const f of incoming) byExt.set(extOf(f.name), f);
      return Array.from(byExt.values());
    });
  }, []);

  const haveRequired = REQUIRED_EXTS.every((ext) => files.some((f) => extOf(f.name) === ext));

  const actionLabel = (a: SubParcelParseResponse['preview'][number]['action']) =>
    a === 'create'
      ? t('sub.actionCreate', { defaultValue: 'nou' })
      : a === 'update'
        ? t('sub.actionUpdate', { defaultValue: 'actualizat' })
        : t('sub.actionUnchanged', { defaultValue: 'neschimbat' });

  const onParse = async () => {
    try {
      setParsed(await parseMut.mutateAsync({ files }));
      setStep('preview');
    } catch (e) {
      pushToast({ variant: 'error', message: (await apiError(e)).message });
    }
  };

  const onCommit = async () => {
    if (!parsed) return;
    try {
      setCommitted(await commitMut.mutateAsync({ batchId: parsed.batchId, mode }));
      setStep('done');
    } catch (e) {
      pushToast({ variant: 'error', message: (await apiError(e)).message });
    }
  };

  const reset = () => {
    setFiles([]);
    setParsed(null);
    setCommitted(null);
    setStep('upload');
  };

  return (
    <div className="space-y-6">
      {step === 'upload' && (
        <div className="space-y-4">
          <div className="rounded-md border border-orange-200 bg-orange-50 p-3 text-xs text-orange-900">
            {t('sub.hint', {
              defaultValue:
                'Fiecare poligon din fișier este un subpoligon, cu UCF-ul complet în coloana UCF_sub (ex. 1A1 = poligonul 1A, subpoligonul 1). Poligonul părinte trebuie să fie deja importat; subpoligonul trebuie să fie în interiorul lui și să nu se suprapună cu alte subpoligoane. Un subpoligon care există deja își actualizează conturul și suprafața din acest fișier.',
            })}
          </div>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              addFiles(e.dataTransfer.files);
            }}
            className={cn(
              'relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 transition-all',
              dragOver
                ? 'border-orange-500 bg-orange-50'
                : 'border-slate-300 bg-slate-50 hover:border-slate-400 dark:border-slate-600 dark:bg-slate-800/60',
            )}
          >
            <Upload className="mb-3 h-10 w-10 text-slate-400" />
            <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
              {t('shp.dropHint', { defaultValue: 'Trage și plasează .shp + .dbf + .prj + .shx' })}
            </p>
            <input
              type="file"
              multiple
              accept=".shp,.dbf,.prj,.shx"
              onChange={(e) => addFiles(e.target.files)}
              className="absolute inset-0 cursor-pointer opacity-0"
            />
          </div>

          {files.length > 0 && (
            <Card>
              <CardBody className="p-0">
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {files.map((f) => (
                    <li key={f.name} className="flex items-center justify-between px-4 py-2 text-sm">
                      <span className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4 text-orange-500" />
                        {f.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => setFiles((prev) => prev.filter((x) => x !== f))}
                        className="rounded p-1 text-red-500 hover:bg-red-50"
                        aria-label={t('common:action.delete')}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}

          <div className="flex justify-end">
            <Button onClick={onParse} loading={parseMut.isPending} disabled={!haveRequired}>
              {t('common:action.next')}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {step === 'preview' && parsed && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={t('sub.toCreate', { defaultValue: 'Noi' })} value={parsed.toCreate} />
            <Stat label={t('sub.toUpdate', { defaultValue: 'De actualizat' })} value={parsed.toUpdate} />
            <Stat label={t('sub.unchanged', { defaultValue: 'Neschimbate' })} value={parsed.unchanged} />
            <Stat
              label={t('preview.invalid', { defaultValue: 'Invalide' })}
              value={parsed.invalidCount}
              danger={parsed.invalidCount > 0}
            />
          </div>

          {!parsed.reprojected && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              {t('shp.reprojectWarning', {
                defaultValue:
                  '.prj nu a fost recunoscut (așteptat MOLDREF99). Coordonatele au rămas neproiectate — încărcați .prj pentru rezultat corect.',
              })}
            </div>
          )}

          {parsed.preview.length > 0 && (
            <Card>
              <CardBody className="max-h-72 overflow-auto p-0">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase text-slate-500 dark:bg-slate-800">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-semibold">UCF</th>
                      <th className="px-2 py-1.5 text-left font-semibold">
                        {t('sub.parent', { defaultValue: 'Poligon părinte' })}
                      </th>
                      <th className="px-2 py-1.5 text-left font-semibold">Primăria</th>
                      <th className="px-2 py-1.5 text-right font-semibold">ha</th>
                      <th className="px-2 py-1.5 text-left font-semibold">
                        {t('sub.action', { defaultValue: 'Acțiune' })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.preview.map((r) => (
                      <tr key={r.rawIndex} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="px-2 py-1.5 font-mono font-semibold text-orange-700">{r.ucf}</td>
                        <td className="px-2 py-1.5 font-mono text-[11px]">{r.parentKey}</td>
                        <td className="px-2 py-1.5">{r.primarie}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{r.suprafata.toFixed(2)}</td>
                        <td className="px-2 py-1.5">
                          <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-semibold', ACTION_CLASS[r.action])}>
                            {actionLabel(r.action)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          )}

          {parsed.columns ? <ImportColumnCheck columns={parsed.columns} /> : null}

          <ImportProblems problems={parsed.errors} fileName="probleme-subpoligoane.csv" />

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button variant="secondary" onClick={() => setStep('upload')}>
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {t('common:action.back')}
            </Button>
            <div className="flex items-center gap-2">
              <Select value={mode} onChange={(e) => setMode(e.target.value as ImportMode)} uiSize="sm">
                <option value="skip_invalid">
                  {t('commit.modeSkip', { defaultValue: 'Sari peste rânduri invalide' })}
                </option>
                <option value="all_or_nothing">{t('commit.modeAll', { defaultValue: 'Tot sau nimic' })}</option>
              </Select>
              <Button
                onClick={onCommit}
                loading={commitMut.isPending}
                disabled={parsed.toCreate + parsed.toUpdate === 0}
              >
                {t('common:action.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {step === 'done' && committed && (
        <Card>
          <CardBody className="space-y-3 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 text-orange-600">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <h2 className="text-lg font-semibold">{t('done.title', { defaultValue: 'Import finalizat' })}</h2>
            <p className="text-sm text-slate-600">
              {t('sub.doneSummary', {
                defaultValue: 'Noi: {{imported}} · Actualizate: {{updated}} · Neschimbate: {{unchanged}} · Sărite: {{skipped}}',
                imported: committed.imported,
                updated: committed.updated ?? 0,
                unchanged: committed.unchanged ?? 0,
                skipped: committed.skipped,
              })}
            </p>
            {committed.errors.length > 0 && (
              <div className="pt-2 text-left">
                <ImportProblems
                  problems={committed.errors.map((e) => ({
                    rawIndex: e.row,
                    code: 'commit',
                    reason: e.message,
                  }))}
                  fileName="probleme-salvare.csv"
                />
              </div>
            )}
            <div className="flex justify-center gap-3 pt-2">
              <Button variant="secondary" onClick={() => navigate('/app/filter')}>
                {t('sub.viewMap', { defaultValue: 'Vezi pe hartă' })}
              </Button>
              <Button onClick={reset}>{t('done.again', { defaultValue: 'Import nou' })}</Button>
            </div>
          </CardBody>
        </Card>
      )}

      {(parseMut.isPending || commitMut.isPending) && (
        <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
          <Spinner /> {t('common:state.loading', { defaultValue: 'Se procesează…' })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, danger }: { label: string; value: number; danger?: boolean }) {
  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2',
        danger ? 'border-red-200 bg-red-50 text-red-700' : 'border-slate-200 bg-slate-50 text-slate-700',
      )}
    >
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
